import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { SiteWixCodeSdkSiteConfig } from '@wix/thunderbolt-symbols'

const prefixTypes = {
	ROUTER_PREFIX_TYPE: 'router',
	DYNAMIC_PAGE_PREFIX_TYPE: 'dynamicPages',
} as const

type RouterPrefixData = {
	name: string
	prefix: string
	type: typeof prefixTypes.ROUTER_PREFIX_TYPE | typeof prefixTypes.DYNAMIC_PAGE_PREFIX_TYPE
}

const siteWixCodeSdkSiteConfig: SiteConfigFn<SiteWixCodeSdkSiteConfig> = ({ api, funcLib }) => {
	const hasPlatform = api.features.isActive('platform')

	if (!hasPlatform) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	const routersConfig = api.rendererModel.getRouters()?.configMap || {}

	const pageIdToPrefix: Record<string, string> = {}
	for (const router of Object.values(routersConfig)) {
		const prefix = router.prefix
		const pages = router.pages || {}
		for (const pageId of Object.values(pages)) {
			pageIdToPrefix[pageId] = prefix
		}
	}

	const routerPrefixes: Record<string, RouterPrefixData> = {}
	for (const [routerId, router] of Object.entries(routersConfig)) {
		const prefix = router.prefix
		const appDefinitionId = router.appDefinitionId
		const type = appDefinitionId === 'wix-code' ? prefixTypes.ROUTER_PREFIX_TYPE : prefixTypes.DYNAMIC_PAGE_PREFIX_TYPE
		const pages = router.pages || {}

		for (const [pageRef, pageId] of Object.entries(pages)) {
			routerPrefixes[pageRef] = {
				name: prefix,
				prefix: `/${prefix}`,
				type,
			}
		}
	}

	const pagesMap = api.rendererModel.getPagesMap()
	const pageIdToTitle: Record<string, string> = {}
	for (const [pageId, pageData] of Object.entries(pagesMap)) {
		pageIdToTitle[pageId] = pageData.title
	}

	const urlMappingsStr = api.rendererModel.getUrlMappings()
	const urlMappings = urlMappingsStr ? funcLib.parseJSON(urlMappingsStr) : null

	const sitePropertiesInfo = api.rendererModel.getSitePropertiesInfo()
	const siteDisplayName = sitePropertiesInfo?.siteDisplayName as string
	const regionalSettings = sitePropertiesInfo?.locale as string
	const language = sitePropertiesInfo?.language || api.rendererModel.getLanguageCode() || ''

	const currency = sitePropertiesInfo?.currency as string
	const timezone = sitePropertiesInfo?.timeZone as string

	const siteRevision = api.publicModel.getSiteRevision()
	const mainPageId = api.rendererModel.getMainPageId()

	const siteConfig: SiteWixCodeSdkSiteConfig = {
		fontFaceServerUrl: 'https://serverless.parastorage.com/_serverless/site-sdk-server/v1/style',
		siteDisplayName,
		siteRevision,
		regionalSettings,
		language,
		currency,
		mainPageId,
		pageIdToPrefix,
		routerPrefixes,
		timezone,
		pageIdToTitle,
		urlMappings,
		viewMode: 'Site',
	}

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig,
	}
}

const name = 'siteWixCodeSdk'

export const config = { site: siteWixCodeSdkSiteConfig, name }
